import { useQuery } from "@tanstack/react-query";
import { GameLaunchResponse } from "../types/casino";
import apiService from "./apiService";
import { launchVeritralGame } from "./veritralService";

export interface GameItem {
  id: string;
  game_id: string;
  image: string;
  key: string;
  code: string;
  provider: string;
  providerName: string;
  providerNameKo: string;
  names: { ko: string; en: string };
  category: string;
  isNew: boolean;
  isFeatured: boolean;
  isPopular: boolean;
}

export interface ProviderFilter {
  code: string;
  name: string;
  nameKo: string;
  logo: string;
  gameCount: number;
}

export interface CategoryFilter {
  name: string;
  count: number;
}

export interface AllGamesResponse {
  status: number;
  games: GameItem[];
  providers: ProviderFilter[];
  categories: CategoryFilter[];
  pagination: {
    page: number;
    perPage: number;
    totalPages: number;
    totalCount: number;
  };
  message: { success: string; warning: string | null };
}

export interface AllGamesParams {
  provider?: string;
  category?: string;
  search?: string;
  featured?: boolean;
  popular?: boolean;
  isNew?: boolean;
  page?: number;
  perPage?: number;
  sort?: "popular" | "newest" | "name" | "provider";
}

export async function getAllGames(
  params: AllGamesParams = {}
): Promise<AllGamesResponse> {
  const response = await apiService.postRaw<AllGamesResponse>(
    "/api/Casino/getAllGames",
    {
      provider: params.provider || undefined,
      category: params.category || undefined,
      search: params.search || undefined,
      featured: params.featured || undefined,
      popular: params.popular || undefined,
      isNew: params.isNew || undefined,
      page: params.page || 1,
      perPage: params.perPage || 50,
      sort: params.sort || "popular",
    }
  );

  if (response?.status !== 0) {
    throw new Error(response?.message?.warning || "Failed to get games");
  }

  return response;
}

export function useAllCasinoGames(params: AllGamesParams = {}) {
  return useQuery({
    queryKey: ["casino", "allGames", params],
    queryFn: () => getAllGames(params),
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    retry: 3,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
  });
}

/** Minimal shape for launch (pages send game row + provider fields). */
export type CasinoLaunchPayload = {
  code: string;
  vendor?: string;
  api?: string;
  id?: string;
  game_id?: string;
  [key: string]: unknown;
};

export async function launchGame(
  lobbyItem: CasinoLaunchPayload,
  _loginId: string,
  _token: string
): Promise<GameLaunchResponse> {
  const gameId = lobbyItem.code;
  const vendor = lobbyItem.vendor || lobbyItem.api;
  if (!gameId || !vendor) {
    throw new Error("Missing game or provider");
  }
  const response = await launchVeritralGame(String(gameId), String(vendor));
  const link = response?.result?.link ?? "";
  if (!link) {
    throw new Error("Launch succeeded but no game URL was returned");
  }
  return { result: { link } };
}

export interface CasinoBettingHistoryRow {
  id: string;
  gameName: string | null;
  providerName: string | null;
  roundId: string;
  betAmount: number;
  winAmount: number;
  status: number;
  createdAt: string;
}

export interface CasinoBettingHistoryResponse {
  status: number;
  histories: CasinoBettingHistoryRow[];
  page: number;
  totalPage: number;
  totalCount: number;
}

export async function getCasinoBettingHistory(
  params: Record<string, unknown>
): Promise<CasinoBettingHistoryResponse> {
  return apiService.postRaw("/api/Casino/getBettingHistory", params);
}

export function useCasinoBettingHistory(
  params: Record<string, unknown>,
  enabled: boolean = true
) {
  return useQuery({
    queryKey: ["casino", "casinoBettingHistory", params],
    queryFn: () => getCasinoBettingHistory(params),
    enabled: apiService.isAuthenticated() && enabled,
    staleTime: 2 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });
}
